import { useState } from 'react'
import { stripPayerIdSuffix } from '../utils/formatters'
import type { EligibilityResult } from '../types'
import { selectPrimaryAAA, aaaEntityLabel } from '../types'
import { hasProviderEnrollmentReject } from '../../shared/eligibilityTypes'
import { REJECTION_GUIDANCE, PROVIDER_ENROLLMENT_GUIDANCE } from '../constants/rejectionGuidance'
import { scrubVendorText } from '../utils/scrubVendorText'

interface RejectionBannerProps {
  result: EligibilityResult
}

export default function RejectionBanner({ result }: RejectionBannerProps) {
  const [showDetails, setShowDetails] = useState(false)

  const aaaSegments = result.aaaSegments ?? []
  const stediWarnings = result.stediWarnings ?? []
  const primaryAAA = selectPrimaryAAA(aaaSegments)
  const category = result.rejection?.category ?? 'other'
  const guidance = REJECTION_GUIDANCE[category]
  // Provider-level rejection (AAA 41/43/44): the payer is refusing the
  // provider, not the patient — show product-owned enrollment guidance
  // instead of the vendor's raw resolution text (#103).
  const isProviderEnrollmentReject = hasProviderEnrollmentReject(
    aaaSegments.map(a => a.rejectReasonCode),
  )
  // Gate on the scrubbed value: a URL-only message scrubs to '' and must not
  // render a bare "Reason:" label.
  const scrubbedReason = scrubVendorText(result.rejection?.message || primaryAAA?.rejectReason)

  return (
    <div className="slideout-panel__lookup-error">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
      </svg>

      <p className="slideout-panel__lookup-error-title">Payer Rejected Request</p>

      {scrubbedReason && (
        <p className="slideout-panel__lookup-error-reason">
          Reason: {scrubbedReason}
        </p>
      )}

      {/* Provider-enrollment rejects get ONLY the enrollment guidance — the
          generic "verify member ID/DOB" line would contradict it (#103). */}
      {isProviderEnrollmentReject ? (
        <p className="slideout-panel__lookup-error-resolution">
          {PROVIDER_ENROLLMENT_GUIDANCE}
        </p>
      ) : (
        <p className="slideout-panel__lookup-error-text">{guidance}</p>
      )}

      {aaaSegments.length > 0 && (
        <button
          className="slideout-panel__lookup-error-toggle"
          onClick={() => setShowDetails(prev => !prev)}
        >
          Technical Details
          <svg
            width="12" height="12" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2"
            style={{ transform: showDetails ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      )}

      {showDetails && (
        <div className="slideout-panel__lookup-error-details">
          <div className="lookup-error-details__reference">
            <span>Payer: {stripPayerIdSuffix(result.payer.name)}</span>
            {result.traceId && (
              <span> &middot; Trace ID: {result.traceId}</span>
            )}
          </div>

          {aaaSegments.map((aaa, i) => {
            const isPrimary = i === 0
            const scrubbedSegmentReason = scrubVendorText(aaa.rejectReason)
            const scrubbedResolutions = scrubVendorText(aaa.possibleResolutions)
            return (
              <div key={i}>
                {!isPrimary && i === 1 && (
                  <div className="lookup-error-details__other-label">Other issues</div>
                )}
                <div
                  className={`lookup-error-details__segment${isPrimary ? ' lookup-error-details__segment--primary' : ''}`}
                >
                  <div className="lookup-error-details__segment-header">
                    <span className="lookup-error-details__entity">
                      {aaaEntityLabel(aaa.entityIdentifierCode)}
                    </span>
                    {aaa.rejectReasonCode && (
                      <span className="lookup-error-details__code">
                        Code: {aaa.rejectReasonCode}
                      </span>
                    )}
                  </div>
                  {scrubbedSegmentReason && (
                    <div className="lookup-error-details__reason">
                      {scrubbedSegmentReason}
                    </div>
                  )}
                  {scrubbedResolutions && (
                    <div className="lookup-error-details__resolution">
                      {scrubbedResolutions}
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          {stediWarnings.length > 0 && (
            <div className="lookup-error-details__warnings">
              <div className="lookup-error-details__warnings-title">Warnings</div>
              {stediWarnings.map((w, i) => (
                <div key={i} className="lookup-error-details__warning">
                  {scrubVendorText(w.description)}
                  {w.code && <span className="lookup-error-details__warning-code"> ({w.code})</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
